import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { API_BASE_URL } from '../shared/api/api.tokens';
import { REQUIRE_AUTH } from '../shared/interceptors/auth-context';
import {
  ServicePayment,
  GenerateLinkResponse,
  ClientPaymentHistory,
  ClientCreditMovements,
  CreateProductSaleRequest,
  CreateProductSaleResponse,
} from '../shared/interfaces';

export interface CreateEpaycoSessionPayload {
  amount: number;
  currency: string;
  description: string;
  country: string;
  clientIp: string;
  payerFirstName: string;
  payerLastName: string;
  payerEmail: string;
  payerPhone: string;
  planCode: string;
  planName?: string;
  billingCycle?: string;
  responseUrl?: string;
  webhookUrl?: string;
  testMode?: boolean;
}

@Injectable({ providedIn: 'root' })
export class PaymentsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  createEpaycoSession(payload: CreateEpaycoSessionPayload) {
    return this.http.post<{ sessionId: string }>(
      `${this.baseUrl}/api/v1/membership/epayco/session`,
      payload,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  createServicePayment(payload: {
    eventId: string;
    clientId: string;
    serviceId: string;
    amount?: number;
    currency?: string;
    sessionPackageId?: string;
  }) {
    return this.http.post<ServicePayment>(
      `${this.baseUrl}/api/v1/payments`,
      payload,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  generateServicePaymentLink(
    paymentId: string,
    payload: { returnUrl: string; notifyUrl: string; sendViaWhatsapp?: boolean }
  ) {
    return this.http.post<GenerateLinkResponse>(
      `${this.baseUrl}/api/v1/payments/${paymentId}/links`,
      payload,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  registerManualPayment(
    paymentId: string,
    payload: { amount: number; method: string; notes?: string; useClientCredit?: boolean }
  ) {
    const body = {
      paymentId,
      amount: payload.amount,
      method: payload.method,
      ...(payload.notes ? { notes: payload.notes } : {}),
      ...(payload.useClientCredit ? { useClientCredit: true } : {}),
    };

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(
      `${this.baseUrl}/api/v1/payments/${paymentId}/manual`,
      body,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
        headers,
      }
    );
  }

  addPaymentInstallment(
    paymentId: string,
    payload: { amount: number; method: string; notes?: string }
  ) {
    return this.http.post(
      `${this.baseUrl}/api/v1/payments/${paymentId}/installments`,
      payload,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  getClientPaymentHistory(clientId: string) {
    return this.http.get<ClientPaymentHistory>(
      `${this.baseUrl}/api/v1/payments/clients/${clientId}/history`,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  addClientCredit(clientId: string, payload: { amount: number; description?: string }) {
    return this.http.post(
      `${this.baseUrl}/api/v1/payments/clients/${clientId}/credit`,
      payload,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  deductClientCredit(clientId: string, payload: { amount: number; reason: string }) {
    return this.http.post<{ clientId: string; balance: number }>(
      `${this.baseUrl}/api/v1/payments/clients/${clientId}/credit/deduct`,
      payload,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  getClientCreditMovements(clientId: string, page = 1, pageSize = 20) {
    return this.http.get<ClientCreditMovements>(
      `${this.baseUrl}/api/v1/payments/clients/${clientId}/credit/movements`,
      {
        params: { page, pageSize },
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  createProductSale(payload: CreateProductSaleRequest) {
    return this.http.post<CreateProductSaleResponse>(
      `${this.baseUrl}/api/v1/payments/product-sales`,
      payload,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  addProductsToPayment(
    paymentId: string,
    payload: { products: Array<{ productId: string; quantity: number; unitPrice: number }> }
  ) {
    return this.http.post<{ paymentId: string; newAmount: number; newPendingAmount: number }>(
      `${this.baseUrl}/api/v1/payments/${paymentId}/products`,
      { products: payload.products },
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }
}
